// Flight-planning engine (spec §8.4) — drone-player's REFERENCE TS implementation. Ported from and
// kept in lockstep with the shared Rust `dp-engine` via the shared `spec/fixtures` (tests/conformance/
// planning.test.ts runs the same vectors the Rust `cargo test` and Swift `swift test` pass, so the
// three apps cannot drift). PURE logic — the app fetches the inputs (see fetch.ts) and calls these.

export interface Weather {
  wind_speed_ms: number;
  wind_gust_ms: number;
  precipitation_mm: number;
  visibility_m: number;
}
export interface Limits {
  max_wind_ms: number;
  max_gust_ms: number;
  max_precip_mm: number;
  min_visibility_m: number;
}
/** Conservative default for a Mini-class aircraft (§8.4.1). */
export const SMALL_DRONE: Limits = { max_wind_ms: 8, max_gust_ms: 10, max_precip_mm: 0.2, min_visibility_m: 1500 };

export interface Reason {
  metric: string;
  severity: string; // "over" (→ no-fly) | "marginal"
  value: number;
  limit: number;
}
export interface Conditions {
  level: string; // "good" | "marginal" | "nofly"
  flyable: boolean;
  reasons: Reason[];
}

/** A parsed airspace zone (SHGM public ArcGIS layer 2). `area_type` 909 = Kırmızı/no-fly, 908 = Yeşil. */
export interface Zone {
  id: string;
  name: string;
  area_type: number | null;
  authority: number | null;
  rings: number[][][]; // rings of [lon, lat]; ring 0 exterior, rest holes/parts
}

/** A temporary flight restriction (SHGM public ArcGIS layer 3, §8.4.5). */
export interface Notam {
  id: string;
  name: string;
  description: string;
  area_type: string; // aviation class: P / R / D / CTR / TMA_P / …
  start_ms: number;
  end_ms: number;
  lower_limit: number;
  upper_limit: number;
  rings: number[][][];
}

export interface LaunchAssessment {
  level: string;
  flyable: boolean;
  weather: Conditions;
  nofly_zones: string[];
  active_notams: string[];
}

export const AREA_NOFLY = 909;
export const AREA_ALLOWED = 908;

// ---- geometry -----------------------------------------------------------------------------------

/** Even-odd ray-cast: is `(lon, lat)` inside the ring (`[lon, lat]` vertices)? */
export function pointInRing(ring: number[][], lon: number, lat: number): boolean {
  const n = ring.length;
  if (n < 3) return false;
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Point inside a multi-ring polygon (holes subtract via even-odd across all rings). */
function ringsContain(rings: number[][][], lon: number, lat: number): boolean {
  return rings.filter((r) => pointInRing(r, lon, lat)).length % 2 === 1;
}

/** Extract an Esri feature's polygon rings as `[lon, lat]` rings (≥3 points each). */
function featureRings(feat: any): number[][][] {
  const raw = feat?.geometry?.rings;
  if (!Array.isArray(raw)) return [];
  const rings: number[][][] = [];
  for (const r of raw) {
    if (!Array.isArray(r)) continue;
    const ring: number[][] = [];
    for (const p of r) {
      if (Array.isArray(p) && typeof p[0] === 'number' && typeof p[1] === 'number') ring.push([p[0], p[1]]);
    }
    if (ring.length >= 3) rings.push(ring);
  }
  return rings;
}

// ---- §8.4.1 weather assessment ------------------------------------------------------------------

export function flightConditions(w: Weather, lim: Limits = SMALL_DRONE): Conditions {
  const reasons: Reason[] = [];
  const flag = (metric: string, value: number, limit: number, over: boolean, marginal: boolean) => {
    const severity = over ? 'over' : marginal ? 'marginal' : null;
    if (severity) reasons.push({ metric, severity, value, limit });
  };
  flag('wind', w.wind_speed_ms, lim.max_wind_ms, w.wind_speed_ms > lim.max_wind_ms, w.wind_speed_ms >= lim.max_wind_ms * 0.8);
  flag('gust', w.wind_gust_ms, lim.max_gust_ms, w.wind_gust_ms > lim.max_gust_ms, w.wind_gust_ms >= lim.max_gust_ms * 0.8);
  flag('precipitation', w.precipitation_mm, lim.max_precip_mm, w.precipitation_mm > lim.max_precip_mm, false);
  flag('visibility', w.visibility_m, lim.min_visibility_m, w.visibility_m < lim.min_visibility_m, w.visibility_m <= lim.min_visibility_m * 1.25);

  const level = reasons.some((r) => r.severity === 'over') ? 'nofly' : reasons.some((r) => r.severity === 'marginal') ? 'marginal' : 'good';
  return { level, flyable: level !== 'nofly', reasons };
}

// ---- §8.4.4 Open-Meteo → Weather (unit normalization) -------------------------------------------

function windToMs(v: number, unit: string): number {
  switch (unit) {
    case 'km/h':
    case 'kmh':
      return v / 3.6;
    case 'mph':
      return v * 0.44704;
    case 'kn':
    case 'kts':
    case 'knots':
      return v * 0.514444;
    default:
      return v; // m/s or unknown
  }
}
function distToM(v: number, unit: string): number {
  return unit === 'ft' || unit === 'feet' ? v * 0.3048 : v;
}

/** Parse an Open-Meteo `/v1/forecast` body → Weather in m/s. Normalizes the km/h default from
 *  `current_units`, so a forgotten `wind_speed_unit=ms` can't cause a false no-fly. */
export function fromOpenMeteo(resp: any): Weather | null {
  const cur = resp?.current;
  if (!cur || typeof cur.wind_speed_10m !== 'number') return null;
  const units = resp.current_units ?? {};
  const u = (k: string): string => (typeof units[k] === 'string' ? units[k] : '');
  // Normalize each field with ITS OWN unit. Gust falls back to the ALREADY-NORMALIZED wind (m/s) —
  // not the raw number, which would skip the km/h→m/s conversion and inflate the gust into a false no-fly.
  const windMs = windToMs(cur.wind_speed_10m, u('wind_speed_10m'));
  return {
    wind_speed_ms: windMs,
    wind_gust_ms: typeof cur.wind_gusts_10m === 'number' ? windToMs(cur.wind_gusts_10m, u('wind_gusts_10m')) : windMs,
    precipitation_mm: typeof cur.precipitation === 'number' ? cur.precipitation : 0,
    visibility_m: distToM(typeof cur.visibility === 'number' ? cur.visibility : 100000, u('visibility')),
  };
}

// ---- §8.4.3 zone parsing / classification -------------------------------------------------------

export function parseEsriFeatures(resp: any): Zone[] {
  const features = resp?.features;
  if (!Array.isArray(features)) return [];
  const out: Zone[] = [];
  features.forEach((feat: any, i: number) => {
    const rings = featureRings(feat);
    if (rings.length === 0) return;
    const a = feat.attributes ?? {};
    const int = (k: string): number | null => (typeof a[k] === 'number' ? a[k] : null);
    const str = (k: string): string | null => (typeof a[k] === 'string' ? a[k] : null);
    const id =
      ['OBJECTID', 'ActorID', 'ReservedAreaID', 'LocationID'].map((k) => int(k)).find((v) => v !== null)?.toString() ??
      str('Name') ??
      str('AdiNumarasi') ??
      String(i);
    out.push({
      id,
      name: str('Name') ?? str('AdiNumarasi') ?? '',
      area_type: int('AreaType'),
      authority: int('Authority'),
      rings,
    });
  });
  return out;
}

export const isNoFly = (z: Zone): boolean => z.area_type === AREA_NOFLY;

/** Collapse zones recurring across province crawls, keeping the first occurrence of each id. */
export function dedupZones(zones: Zone[]): Zone[] {
  const seen = new Set<string>();
  return zones.filter((z) => (seen.has(z.id) ? false : (seen.add(z.id), true)));
}

const zoneContains = (z: Zone, lon: number, lat: number): boolean => ringsContain(z.rings, lon, lat);

export function zonesAt(zones: Zone[], lat: number, lon: number): Zone[] {
  return zones.filter((z) => zoneContains(z, lon, lat));
}
export function noFlyZonesAt(zones: Zone[], lat: number, lon: number): Zone[] {
  return zones.filter((z) => isNoFly(z) && zoneContains(z, lon, lat));
}

// Coarse 0.1° (~11 km) spatial grid over anything carrying polygon rings. A point lookup then tests only
// the handful of items whose bbox covers the point's cell, instead of scanning the whole ~11k-zone /
// thousands-of-NOTAM array on every mousemove. Rebuild (new RingGrid(items)) whenever the dataset changes.
const GRID_CELL = 0.1;
function ringsBbox(rings: number[][][]): [number, number, number, number] {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const r of rings)
    for (const [x, y] of r) {
      if (x < x0) x0 = x;
      if (y < y0) y0 = y;
      if (x > x1) x1 = x;
      if (y > y1) y1 = y;
    }
  return [x0, y0, x1, y1];
}
export class RingGrid<T extends { rings: number[][][] }> {
  private grid = new Map<string, T[]>();
  constructor(items: T[]) {
    for (const it of items) {
      const bb = ringsBbox(it.rings);
      if (!Number.isFinite(bb[0])) continue;
      for (let x = Math.floor(bb[0] / GRID_CELL); x <= Math.floor(bb[2] / GRID_CELL); x++)
        for (let y = Math.floor(bb[1] / GRID_CELL); y <= Math.floor(bb[3] / GRID_CELL); y++) {
          const k = `${x}:${y}`;
          const a = this.grid.get(k);
          if (a) a.push(it);
          else this.grid.set(k, [it]);
        }
    }
  }
  /** Items whose polygon contains the point — bbox-pruned by the grid cell, then exact even-odd ring test. */
  at(lat: number, lon: number): T[] {
    const cand = this.grid.get(`${Math.floor(lon / GRID_CELL)}:${Math.floor(lat / GRID_CELL)}`);
    return cand ? cand.filter((it) => ringsContain(it.rings, lon, lat)) : [];
  }
}

// ---- §8.4.2 airspace membership -----------------------------------------------------------------

export function pointInAirspaces(lat: number, lon: number, airspaces: { id: string; polygon: number[][] }[]): string[] {
  return airspaces.filter((a) => pointInRing(a.polygon, lon, lat)).map((a) => a.id);
}

// ---- §8.4.5 NOTAM -------------------------------------------------------------------------------

export function parseNotamFeatures(resp: any): Notam[] {
  const features = resp?.features;
  if (!Array.isArray(features)) return [];
  const out: Notam[] = [];
  features.forEach((feat: any, i: number) => {
    const rings = featureRings(feat);
    if (rings.length === 0) return;
    const a = feat.attributes ?? {};
    const str = (k: string): string => (typeof a[k] === 'string' ? a[k] : '');
    const int = (k: string): number => (typeof a[k] === 'number' ? a[k] : 0);
    out.push({
      id: typeof a.OBJECTID === 'number' ? String(a.OBJECTID) : String(i),
      name: str('Name'),
      description: str('Description'),
      area_type: str('AreaType'),
      start_ms: int('StartDate'),
      end_ms: int('EndDate'),
      lower_limit: int('LowerLimit'),
      upper_limit: int('UpperLimit'),
      rings,
    });
  });
  return out;
}

export const isActive = (n: Notam, timeMs: number): boolean => n.start_ms <= timeMs && timeMs <= n.end_ms;

/**
 * The next instant at which the ACTIVE set of NOTAMs changes, or null when it never changes again.
 *
 * Everything that filters NOTAMs by their window — the raw map layer, the merged effective geometry,
 * the search index — evaluates `Date.now()` once, at the moment the data is pushed. Nothing revisits
 * it, so that single evaluation stands for as long as the dataset id does: up to the 12 h NOTAM trust
 * window, and longer again for a merge persisted across sessions. A restriction that BEGINS inside
 * that window is then simply missing from a map still presenting its NOTAM layer as trustworthy,
 * which is the one direction of error that matters on a map people fly from.
 *
 * Both edges count: a start that has not arrived yet, and one millisecond past an end that has not
 * passed yet (the window is inclusive at both ends, per isActive).
 */
export function nextNotamChange(notams: Notam[], timeMs: number): number | null {
  let next = Infinity;
  for (const n of notams) {
    if (n.start_ms > timeMs && n.start_ms < next) next = n.start_ms;
    const off = n.end_ms + 1;
    if (off > timeMs && off < next) next = off;
  }
  return Number.isFinite(next) ? next : null;
}

export function activeNotamsAt(notams: Notam[], timeMs: number, lat: number, lon: number): Notam[] {
  return notams.filter((n) => isActive(n, timeMs) && ringsContain(n.rings, lon, lat));
}

// ---- §8.4.6 unified launch assessment -----------------------------------------------------------

export function assessLaunch(
  weather: Weather,
  point: { lat: number; lon: number },
  timeMs: number,
  zones: Zone[],
  notams: Notam[],
  limits: Limits = SMALL_DRONE,
): LaunchAssessment {
  const weatherCond = flightConditions(weather, limits);
  const noflyZones = noFlyZonesAt(zones, point.lat, point.lon).map((z) => z.id);
  const activeNotams = activeNotamsAt(notams, timeMs, point.lat, point.lon).map((n) => n.id);
  const blocked = noflyZones.length > 0 || activeNotams.length > 0;
  return {
    level: blocked ? 'nofly' : weatherCond.level,
    flyable: !blocked && weatherCond.flyable,
    weather: weatherCond,
    nofly_zones: noflyZones,
    active_notams: activeNotams,
  };
}
